import EventModel from '@/common/models/rules/event-model';
import asyncAction, {isExpired, timedSave} from '@/store/utils/async-action';
import GatewayAccountModel from '@/common/models/gateway-account-model';

const tenMinutesDelay = 10 * 60 * 1000;

export default ({apiHandler, Vue}) => {
    const bus = new Vue();
    return {
        namespaced: true,
        state: {
            gateways: null,
            products: null,
            lists: null,
            events: {},
            labels: [],
            customFields: {},
        },
        getters: {
            gateways: state => state.gateways,
            products: state => state.products,
            lists: state => state.lists,
            listOptions: state => (state.lists && state.lists.lists
                ? state.lists.lists.map(({fields}) => ({label: `${fields.id}`, value: fields.id}))
                : []),
            list: state => id => state.lists.lists.find(l => l.fields.id === id),
            systemEvent: state => eventType => state.events[eventType] || null,
            labels: state => state.labels,
            customFields: state => resourceType => state.customFields[resourceType],
        },
        mutations: {
            fetchGateways(state, fetch) {
                state.gateways = fetch;
            },
            fetchProducts(state, fetch) {
                state.products = fetch;
            },
            fetchLists(state, fetch) {
                state.lists = fetch;
            },
            fetchCustomFields(state, {fetch, resourceType}) {
                state.customFields[resourceType] = fetch;
            },
            storeGateways(state, gateways) {
                state.gateways = timedSave({gateways});
            },
            storeProducts(state, products) {
                state.products = timedSave({products});
            },
            storeLists(state, lists) {
                state.lists = timedSave({lists}, tenMinutesDelay);
            },
            storeSystemEvent(state, event) {
                state.events = {
                    ...state.events,
                    [event.eventType]: timedSave({event}),
                };
            },
            storeLabels(state, labels) {
                state.labels = [...new Set(state.labels.concat(labels))];
            },
            storeCustomFields(state, {resourceType, fields}) {
                // save custom fields for 10 minutes
                state.customFields[resourceType] = timedSave({fields}, tenMinutesDelay);
            },
            invalidateCached(state, {type, defaultState = null}) {
                if (Reflect.has(state, type)) {
                    state[type] = defaultState;
                }
            },
        },
        actions: {
            async fetchGatewayAccounts({commit, getters}) {
                return asyncAction({
                    bus,
                    eventName: 'fetchGatewayAccounts',
                    commitName: 'fetchGateways',
                    predicate: getters.gateways,
                    delegate: async () => {
                        const params = {
                            limit: 200,
                            filter: `status:!${GatewayAccountModel.Statuses.closed}`,
                        };
                        const accounts = await apiHandler.client.gatewayAccounts.getAll(params);
                        commit('storeGateways', accounts.getJSON().items);
                    },
                    commit,
                });
            },
            async fetchProducts({commit, getters}) {
                return asyncAction({
                    bus,
                    eventName: 'fetchProducts',
                    commitName: 'fetchProducts',
                    predicate: getters.products,
                    delegate: async () => {
                        const accounts = await apiHandler.client.products.getAll({limit: 100});
                        commit('storeProducts', accounts.getJSON().items);
                    },
                    commit,
                });
            },
            async fetchLists({commit, getters}) {
                return asyncAction({
                    bus,
                    eventName: 'fetchLists',
                    commitName: 'fetchLists',
                    predicate: getters.lists,
                    delegate: async () => {
                        const accounts = await apiHandler.client.lists.getAll({
                            limit: 200,
                            fields: 'id,name',
                            sort: '-updatedTime',
                        });
                        commit('storeLists', accounts.getJSON().items);
                    },
                    commit,
                });
            },
            async fetchSystemEvent({commit, getters}, eventType) {
                if (isExpired(getters.systemEvent(eventType))) {
                    const event = await apiHandler.client.events.get({eventType});
                    commit('storeSystemEvent', new EventModel(event.getJSON().fields));
                }
            },
            async fetchCustomFields({commit, getters}, resourceType) {
                return asyncAction({
                    bus,
                    eventName: 'fetchCustomFields',
                    commitName: 'fetchCustomFields',
                    predicate: getters.customFields(resourceType),
                    delegate: async () => {
                        const customFields = await apiHandler.client.customFields
                            .getAll({resource: resourceType, limit: 100});
                        commit('storeCustomFields', {
                            resourceType,
                            fields: customFields.getJSON().items,
                        });
                    },
                    commit,
                });
            },
            parseLabels({commit}, rules) {
                commit('storeLabels', rules.reduce((arr, rule) => arr.concat(rule.labels), []));
            },
            addLabels({commit}, labels) {
                commit('storeLabels', labels);
            },
            // TODO call invalidation from forms that add to the data
            invalidateGateways({commit}) {
                commit('invalidateCached', {type: 'gateways'});
            },
            invalidateProducts({commit}) {
                commit('invalidateCached', {type: 'products'});
            },
            invalidateLists({commit}) {
                commit('invalidateCached', {type: 'lists'});
            },
            invalidateCustomFields({commit}) {
                commit('invalidateCached', {type: 'customFields', defaultState: {}});
            },
        },
    };
};
